import React, { useState } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import LockOutlinedIcon from '@material-ui/icons/LockOutlined';
import { makeStyles } from '@material-ui/core/styles';

import { API_URL } from '../../../constants/endpoints';
import sessionFetch from '../../../middleware/sessionFetch';
import { actions as authActions } from '../../Auth/actions';
import { NAME as AUTH_NAME } from '../../Auth/reducer';
import { colors } from '../theme';
import FieldLabel from './FieldLabel';

const MIN_LENGTH = 10;

const useStyles = makeStyles({
  box: {
    padding: '12px 22px 6px',
    borderTop: `1px solid ${colors.border}`,
  },
  login: {
    fontWeight: 600,
    fontSize: '13px',
    color: colors.text,
    marginBottom: 4,
    wordBreak: 'break-all',
  },
  action: {
    textTransform: 'none',
    justifyContent: 'flex-start',
    color: colors.textDim,
    fontSize: '13px',
    padding: '2px 0',
    '&:hover': { color: colors.text, backgroundColor: 'transparent' },
  },
  error: { color: '#d32f2f', fontSize: '13px', marginTop: 8 },
  ok: { color: colors.okText, fontSize: '13px' },
  note: { color: colors.textDim, fontSize: '12px', marginTop: 8, lineHeight: 1.45 },
});

// Смена пароля. Сервер завершает остальные входы этого пользователя, этот остаётся.
export function ChangePasswordDialog({ onClose, classes }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(null);

  const handleSave = async () => {
    setError('');
    if (next.length < MIN_LENGTH) {
      setError(`Новый пароль: не короче ${MIN_LENGTH} знаков.`);
      return;
    }
    if (next !== repeat) {
      setError('Новый пароль и его повтор не совпадают.');
      return;
    }
    setBusy(true);
    try {
      const res = await sessionFetch(`${API_URL}/api/auth/password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ current, next }),
      });
      const json = await res.json();
      if (json.status === 'ok') {
        setDone(json.data.otherSessionsEnded || 0);
      } else if (res.status === 429) {
        setError('Слишком много неудачных попыток. Попробуйте позже.');
      } else {
        setError((json.data && json.data.type) || 'Не удалось сменить пароль.');
      }
    } catch (err) {
      setError(`Сервер недоступен: ${err.message}`);
    }
    setBusy(false);
  };

  return (
    <Dialog open onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Сменить пароль</DialogTitle>
      <DialogContent>
        {done === null ? (
          <>
            <FieldLabel label="Текущий пароль">
              <TextField
                fullWidth
                type="password"
                variant="outlined"
                autoComplete="current-password"
                value={current}
                onChange={(event) => setCurrent(event.target.value)}
              />
            </FieldLabel>
            <FieldLabel label="Новый пароль">
              <TextField
                fullWidth
                type="password"
                variant="outlined"
                autoComplete="new-password"
                value={next}
                onChange={(event) => setNext(event.target.value)}
              />
            </FieldLabel>
            <FieldLabel label="Новый пароль ещё раз">
              <TextField
                fullWidth
                type="password"
                variant="outlined"
                autoComplete="new-password"
                value={repeat}
                onChange={(event) => setRepeat(event.target.value)}
              />
            </FieldLabel>
            <Typography className={classes.note}>
              Не короче {MIN_LENGTH} знаков, без вашего логина и без слов вроде «password» или
              «admin». После смены вы останетесь в системе, а на других устройствах придётся войти заново.
            </Typography>
            {error && <div className={classes.error}>{error}</div>}
          </>
        ) : (
          <Typography className={classes.ok}>
            Пароль изменён.
            {done > 0 && ` Другие ваши входы (${done}) завершены.`}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        {done === null ? (
          <>
            <Button onClick={onClose} disabled={busy}>
              Отмена
            </Button>
            <Button
              variant="contained"
              color="primary"
              disableElevation
              disabled={busy || !current || !next || !repeat}
              onClick={handleSave}
            >
              Сменить
            </Button>
          </>
        ) : (
          <Button variant="contained" color="primary" disableElevation onClick={onClose}>
            Готово
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

// Кто вошёл, выход и смена пароля: внизу меню раздела рассылок.
function AccountMenu({ login, logout }) {
  const classes = useStyles();
  const [open, setOpen] = useState(false);

  return (
    <div className={classes.box}>
      {login && <div className={classes.login}>{login}</div>}
      <Button
        fullWidth
        size="small"
        className={classes.action}
        startIcon={<LockOutlinedIcon fontSize="small" />}
        onClick={() => setOpen(true)}
      >
        Сменить пароль
      </Button>
      <Button
        fullWidth
        size="small"
        className={classes.action}
        startIcon={<ExitToAppIcon fontSize="small" />}
        onClick={() => logout()}
      >
        Выйти
      </Button>
      {open && <ChangePasswordDialog classes={classes} onClose={() => setOpen(false)} />}
    </div>
  );
}

const mapStateToProps = (state) => ({ login: state[AUTH_NAME].login });

// Только выход: если привязать все действия, они заслонят свойство login.
const mapDispatchToProps = (dispatch) =>
  bindActionCreators({ logout: authActions.logout }, dispatch);

export default connect(mapStateToProps, mapDispatchToProps)(AccountMenu);
